import { useCallback, useEffect, useMemo, useState } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowUpTrayIcon, CheckIcon, DocumentArrowDownIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { TanTable } from '../../../components/TanTable';
import Notification, { NotificationContainer } from '../../../components/Notification';
import tprmService from '../../../services/compliance/tprm.service';
import { saveAs } from '../../../utils/saveFile';
import VendorImportModal from './VendorImportModal';
import {
  Vendor,
  VendorListFilters,
} from '../../../types/compliance';
import { useUserNames } from '../../../hooks/useUserNames';

const TIER_BADGE: Record<string, string> = {
  low: 'bg-green-200 text-green-800 dark:bg-green-900 dark:text-green-200',
  medium: 'bg-blue-200 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  high: 'bg-amber-200 text-amber-800 dark:bg-amber-900 dark:text-amber-200',
  critical: 'bg-red-200 text-red-800 dark:bg-red-900 dark:text-red-200',
};

const STATUS_BADGE: Record<string, string> = {
  draft: 'bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
  submitted: 'bg-blue-200 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  under_review: 'bg-indigo-200 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200',
  approved: 'bg-green-200 text-green-800 dark:bg-green-900 dark:text-green-200',
  approved_with_conditions: 'bg-amber-200 text-amber-800 dark:bg-amber-900 dark:text-amber-200',
  rejected: 'bg-red-200 text-red-800 dark:bg-red-900 dark:text-red-200',
  archived: 'bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
};

function BoolIcon({ value }: { value: boolean }) {
  return value ? (
    <CheckIcon className="w-4 h-4 text-green-600 dark:text-green-400" />
  ) : (
    <XMarkIcon className="w-4 h-4 text-red-600 dark:text-red-400" />
  );
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString();
  } catch {
    return iso;
  }
}

const BOOL_KEYS = [
  'has_pii',
  'mfa_supported',
  'sso_supported',
  'is_critical_operations',
  'handles_payment',
  'overdue_review_only',
] as const;


const STRING_KEYS = [
  'search',
  'status',
  'risk_tier',
  'service_type',
  'department',
  'owner_oid',
  'annual_cost_range',
  'contract_type',
  'next_review_before',
] as const;


type Props = {
  embedded?: boolean;
};


export default function VendorRegister({ embedded = false }: Props = {}) {
  const navigate = useNavigate();
  const { nameFor } = useUserNames();
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState<Vendor[]>([]);
  const [loading, setLoading] = useState(true);
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [errorNotification, setErrorNotification] =
    useState<{ title: string; message: string } | null>(null);

  // URL-bound so filter state is bookmarkable + shareable.
  const filters: VendorListFilters = useMemo(() => {
    const out: VendorListFilters = {};
    STRING_KEYS.forEach((k) => {
      const v = searchParams.get(k);
      if (v) (out as Record<string, unknown>)[k] = v;
    });
    BOOL_KEYS.forEach((k) => {
      const v = searchParams.get(k);
      if (v === 'true') (out as Record<string, unknown>)[k] = true;
    });
    return out;
  }, [searchParams]);

  const setFilters = useCallback(
    (updater: VendorListFilters | ((prev: VendorListFilters) => VendorListFilters)) => {
      const next = typeof updater === 'function' ? updater(filters) : updater;
      const params = new URLSearchParams(searchParams);
      STRING_KEYS.forEach((k) => {
        const v = (next as Record<string, unknown>)[k];
        if (v) params.set(k, String(v));
        else params.delete(k);
      });
      BOOL_KEYS.forEach((k) => {
        if ((next as Record<string, unknown>)[k]) params.set(k, 'true');
        else params.delete(k);
      });
      setSearchParams(params, { replace: true });
    },
    [filters, searchParams, setSearchParams],
  );

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const rows = await tprmService.listVendors(filters);
      setData(rows);
    } catch (error: any) {
      const msg = error?.response?.data?.detail || error?.message || 'Unknown error occurred';
      setErrorNotification({ title: 'Failed to load vendors', message: msg });
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleDownloadTemplate = useCallback(async () => {
    setDownloadingTemplate(true);
    try {
      await saveAs('vendor-intake-template.xlsx', () => tprmService.downloadTemplate());
    } catch (error: any) {
      const msg = error?.response?.data?.detail || error?.message || 'Unknown error occurred';
      setErrorNotification({ title: 'Failed to download template', message: msg });
    } finally {
      setDownloadingTemplate(false);
    }
  }, []);

  const handleImportError = useCallback((title: string, message: string) => {
    setErrorNotification({ title, message });
  }, []);

  const handleImported = useCallback(
    (vendorId: string) => {
      setImportOpen(false);
      navigate(`/compliance/tprm/vendors/${vendorId}/edit`);
    },
    [navigate],
  );

  const columns = useMemo<ColumnDef<Vendor>[]>(
    () => [
      {
        accessorKey: 'name',
        header: () => <span className="pl-3">Vendor</span>,
        enableSorting: true,
        size: 220,
        cell: ({ row }) => (
          <span className="pl-3">
            <Link
              to={`/compliance/tprm/vendors/${row.original.id}`}
              className="font-semibold text-blue-600 hover:text-blue-800 dark:text-blue-400"
            >
              {row.original.name}
            </Link>
          </span>
        ),
      },
      {
        accessorKey: 'service_type',
        header: 'Service Type',
        enableSorting: true,
        size: 140,
        cell: ({ row }) => (
          <span className="px-2 py-0.5 text-xs font-medium rounded bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200">
            {row.original.service_type || '—'}
          </span>
        ),
      },
      {
        accessorKey: 'department',
        header: 'Department',
        enableSorting: true,
        size: 160,
        cell: ({ row }) => <span>{row.original.department || '—'}</span>,
      },
      {
        // Owner's name as the value (column filter, sorting, search, export);
        // id kept as 'business_owner_oid' so saved table layouts still work.
        id: 'business_owner_oid',
        accessorFn: (v) => nameFor(v.business_owner_oid),
        header: 'Owner',
        enableSorting: true,
        size: 160,
        cell: ({ getValue }) => <span>{getValue<string>() || '—'}</span>,
      },
      {
        id: 'total_score',
        accessorFn: (r) => r.scores?.total_score ?? 0,
        header: 'Score',
        enableSorting: true,
        size: 90,
        cell: ({ row }) => (
          <span>
            {row.original.scores?.total_score != null
              ? row.original.scores.total_score.toFixed(2)
              : '—'}
          </span>
        ),
      },
      {
        id: 'risk_tier',
        accessorFn: (r) => r.scores?.risk_tier ?? '',
        header: 'Tier',
        enableSorting: true,
        size: 110,
        cell: ({ row }) => {
          const tier = row.original.scores?.risk_tier || '';
          if (!tier) return <span>—</span>;
          return (
            <span
              className={`px-2 py-0.5 text-xs font-medium rounded ${TIER_BADGE[tier] || ''}`}
            >
              {tier}
            </span>
          );
        },
      },
      {
        accessorKey: 'status',
        header: 'Status',
        enableSorting: true,
        size: 140,
        cell: ({ row }) => (
          <span
            className={`px-2 py-0.5 text-xs font-medium rounded ${STATUS_BADGE[row.original.status] || ''}`}
          >
            {row.original.status.replace(/_/g, ' ')}
          </span>
        ),
      },
      {
        id: 'has_pii',
        accessorFn: (r) => r.flags?.has_pii ?? false,
        header: 'PII',
        size: 60,
        cell: ({ row }) => <BoolIcon value={!!row.original.flags?.has_pii} />,
      },
      {
        id: 'mfa_supported',
        accessorFn: (r) => r.flags?.mfa_supported ?? false,
        header: 'MFA',
        size: 60,
        cell: ({ row }) => <BoolIcon value={!!row.original.flags?.mfa_supported} />,
      },
      {
        id: 'sso_supported',
        accessorFn: (r) => r.flags?.sso_supported ?? false,
        header: 'SSO',
        size: 60,
        cell: ({ row }) => <BoolIcon value={!!row.original.flags?.sso_supported} />,
      },
      {
        accessorKey: 'next_review_date',
        header: 'Next Review',
        enableSorting: true,
        size: 120,
        cell: ({ row }) => <span>{formatDate(row.original.next_review_date)}</span>,
      },
    ],
    [nameFor],
  );

  // A fresh array whenever the owner names land: TanStack caches each row's
  // cell values until `data` changes, so without this the Owner cells stay
  // blank until the next reload.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const tableData = useMemo(() => [...data], [data, nameFor]);

  return (
    <div className="h-full w-full p-6 pb-24">
      <div className="mb-4 flex items-start justify-between">
        <div>
          {!embedded && (
            <>
              <h1 className="text-2xl font-bold">Vendor Register</h1>
              <p className="text-sm text-muted">
                All third-party vendors tracked for TPRM.
              </p>
            </>
          )}
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={handleDownloadTemplate}
            disabled={downloadingTemplate}
            className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-highlight disabled:opacity-50"
          >
            <DocumentArrowDownIcon className="w-4 h-4" />
            {downloadingTemplate ? 'Downloading...' : 'Download Template'}
          </button>
          <button
            type="button"
            onClick={() => setImportOpen(true)}
            className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-highlight"
          >
            <ArrowUpTrayIcon className="w-4 h-4" />
            Import
          </button>
        </div>
      </div>

      <VendorImportModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onImported={handleImported}
        onError={handleImportError}
      />

      {/* Filters are handled by TanTable's built-in column filters + search.
          URL params (status, risk_tier, etc.) still flow into `filters` and
          pre-filter the initial load — that keeps dashboard and audit-log
          cross-links working. */}

      <TanTable
        data={tableData}
        columns={columns}
        loading={loading}
        settingsKey="compliance-tprm-vendors-table"
        fieldName="compliance-tprm-vendors-table"
        searchPlaceholder="Search vendors..."
        onRowClick={(row) => navigate(`/compliance/tprm/vendors/${row.id}`)}
        enableGrouping={false}
        enableColumnFilters={true}
        cardTitle="Vendors"
        showEditFiltersButton={true}
        editFiltersButtonIcon="plus"
        editFiltersButtonTooltip="New Vendor Intake"
        onEditFilters={() => navigate('/compliance/tprm/vendors/new')}
      />

      {errorNotification && (
        <NotificationContainer position="top-right">
          <Notification
            type="error"
            title={errorNotification.title}
            message={errorNotification.message}
            showClose={true}
            onClose={() => setErrorNotification(null)}
            autoDismiss={5000}
          />
        </NotificationContainer>
      )}
    </div>
  );
}
